import { useState } from "react"
import { FieldType, UIPermission, Column, FieldValue, useUIActions, ModalId, Action, SpinnerDiv, Data, ButtonKind, Badge } from "../../../../../features/ui"
import { DataType } from "../../../../../tables"
import { useSystemStateHooks } from "../../../../system"
import { define, toNumber } from "../../../../../utils/typeUtils"
import { DataTypeList, Labels, useDataTypesHooks } from "../../../../../features/data-types"
import { Expense, Ticket } from "../../../state/types"
import { SalaryFilters } from "./salaryFilters"
import { useLocation } from "react-router-dom"
import { RoutePath } from "../../../../../features/routes"
import { PaymentStatus } from "../../../../operations"
import { DownloadExpenseFilesModal } from "../../download-expense-files-modal/downloadExpenseFilesModal"

type Props = {
    salaries: Expense[]
    tickets: Ticket[]
}

export const SalaryList = (props: Props) => {
    const { salaries, tickets } = props

    const [rows, setRows] = useState<Data[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    const [currentExpense, setCurrentExpense] = useState<Expense>()

    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_EXPENSES)
    const { formatValue } = useDataTypesHooks()
    const { pathname } = useLocation()

    const { openModal } = useUIActions()

    const setRowsState = (expenses: Expense[]) => {
        const newRows = expenses.map(expense => {
            const ticket = tickets.find(ticket => ticket.parentId === expense.id)
            return {
                ...expense,
                ...(ticket && {
                    paymentDate: ticket.dueDate
                })
            }
        })
        setRows(newRows)
        setAsyncDataLoaded(true)
    }

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: '#',
            render: (value?: FieldValue) => `#${value}`
        },
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'paymentDate',
            type: FieldType.DATE,
            label: 'Fecha de Pago',
            render: (value?: FieldValue) => value ?
                formatValue(new Date(value as string), FieldType.DATE) :
                <Badge label="Sin recibo" />
        },
        {
            name: 'amount',
            type: FieldType.PRICE,
            label: 'Monto'
        },
        {
            name: 'paymentStatus',
            type: FieldType.TEXT,
            label: 'Estado de Pago',
            sortValue: (value?: FieldValue) => Labels.paymentStatusLabel(value as PaymentStatus),
            render: (value?: FieldValue) => Labels.paymentStatusLabel(define(value) as PaymentStatus)
        }
    ]

    const findSalary = (code?: string) => salaries.find(salary => salary.code === toNumber(code))
    const findTicket = (parentId?: string) => tickets.find(ticket => ticket.parentId === parentId)

    const onDownloadFiles = (code?: string) => {
        setCurrentExpense(findSalary(code))
        openModal(ModalId.DOWNLOAD_FILES)
    }

    const onRemove = (code?: string) => {
        setCurrentExpense(findSalary(code))
        openModal(ModalId.REMOVE)
    }

    const actions = (code: string): Action[] => {
        const salary = findSalary(code)
        const salaryTicket = findTicket(salary?.id)
        const isPaymentPending = salary?.paymentStatus === PaymentStatus.PENDING
        const payedAmount = salaryTicket?.payedAmount || 0
        const isEditable = isPaymentPending && payedAmount === 0

        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary' as ButtonKind, tooltip: 'Ver Detalle', href: `${pathname}/${salary?.id}` }
        ]
        if (salaryTicket?.ticketUrl) {
            actionList.push({ icon: 'download', kind: 'tertiary', tooltipLines: ['Descargar', 'Documentos'], onClick: onDownloadFiles })
        }
        if (!readOnly && isEditable) {
            const editAction: Action = { icon: 'pencil-fill', tooltip: 'Editar', href: `${pathname}/${salary?.id}/${RoutePath.EDIT}` }
            const removeAction: Action = { icon: 'trash-fill', kind: 'secondary', tooltip: 'Eliminar', onClick: onRemove }
            actionList.push(editAction, removeAction)
        }

        return actionList
    }

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (dataTypes: DataType[]) => {
        setRowsState(dataTypes as Expense[])
    }

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    return (
        <>
            <SalaryFilters
                salaries={salaries}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            {renderAsync(
                <DataTypeList
                    columns={columns}
                    rows={rows}
                    rowId="code"
                    currentDataTypeParam={currentExpense}
                    createLabel="Crear Nuevo"
                    actions={actions}
                    readOnly={readOnly}
                />
            )}
            <DownloadExpenseFilesModal expense={currentExpense} />
        </>
    )
}
